import { CoverOrderInput, GqlBodySide } from "@/types/interfaces/orderManagement/coverOrderInput";
import { mutateWithVariables } from "./api";
import { OrderDetailsInput } from "@/types/interfaces/orderManagement/orderDetailsInput";
import { CoverOrderItemsInput } from "@/types/interfaces/orderManagement/coverOrderItemsInput";
import { OrderRequestData } from "./orderService";
import { useComponentSettingStore } from '@/utils/componentSettings';
import { Component } from "@/types/api/Model/Component";
import { checkUserSession, getDomainByUrlSuffix } from "./userService";
import * as UserService from '@/services/api/userService';
import { Model2 } from "@/types/api/Model/Model";


export const addCoverOrder = async (
  coverOrderInput: CoverOrderInput
): Promise<any> => {
  const addCoverOrderResult = await mutateWithVariables(
    `
      mutation AddCoverOrder($coverOrderInput: CoverOrderInput!) {
        addCoverOrder(coverOrderInput: $coverOrderInput) {
          orderId
          orderNumber
          billingAddress
          shippingAddress
          orderDate
          deliveryDate
          orderStatus
          paymentStatus
          paymentTerms
          totalPrice
          tax
          taxRate
          customerUserId
          customer {
            additionalEmails
            authentikId
            email
            firstname
            lastname
            titlePostfix
            titlePrefix
            userId
            username
          }
          supplierUserId
          supplier {
            additionalEmails
            authentikId
            email
            firstname
            lastname
            titlePostfix
            titlePrefix
            userId
            username
          }
        }
      }
    `,
    { coverOrderInput }
  );

  return addCoverOrderResult.data.addCoverOrder;
};

export const updateCoverOrder = async (
  orderId: number,
  coverOrderInput: CoverOrderInput
): Promise<any> => {
  const updateCoverOrderResult = await mutateWithVariables(
    `
      mutation UpdateCoverOrder($orderId: Long!, $coverOrderInput: CoverOrderInput!) {
        updateCoverOrder(orderId: $orderId, coverOrderInput: $coverOrderInput) {
          orderId
          orderNumber
          billingAddress
          shippingAddress
          orderDate
          deliveryDate
          orderStatus
          paymentStatus
          paymentTerms
          totalPrice
          tax
          taxRate
          customerUserId
          customer {
            additionalEmails
            authentikId
            email
            firstname
            lastname
            titlePostfix
            titlePrefix
            userId
            username
          }
          supplierUserId
          supplier {
            additionalEmails
            authentikId
            email
            firstname
            lastname
            titlePostfix
            titlePrefix
            userId
            username
          }
        }
      }
    `,
    {
      orderId,
      coverOrderInput,
    }
  );

  return updateCoverOrderResult.data.updateCoverOrder;
};


function mapBodySideToGql(value: string): GqlBodySide {
  switch (value?.toLowerCase()) {
    case 'left': return 'LEFT';
    case 'right': return 'RIGHT';
    case 'bilateral': return 'BILATERAL';
    default: return 'NONE';
  }
}

function mapToCoverOrderInput(
  data: OrderRequestData,
  components: Component[],
  isDraft: boolean,
  domainId: number,
  customerUserId: number,
  billingAddressId: number,
  modelId: number
): CoverOrderInput {
  const orderDetails: OrderDetailsInput = {
    domainId,
    customerUserId,
    billingAddressId: billingAddressId ?? 0,
    shippingAddressId: billingAddressId ?? 0,
    quantity: 1,
    modelId,
    isDraft: isDraft,
    scanFiles: data.scanFiles ?? [],
    imageFiles: data.imageFiles ?? [],
    width: data.calfCircumferenceWithoutDeduction ?? null,
    height: data.heightWithoutDeduction ?? null,
    depth: data.depth ?? null,
    additionalInformation: data.remarks || null,
  };

  const componentSettingsStore = useComponentSettingStore();
  const settings = componentSettingsStore.getAllComponentSettings();

  let coverOrderItems: CoverOrderItemsInput[] = components.map(component => {
    const setting = settings.find(s => s[0] === component.componentId)?.[1];
    
    return {
      componentId: component.componentId,
      quantity: 1,
      color: setting?.color,
      engraving: setting?.embossing,
      engravingColor: setting?.embossingColor
    } as CoverOrderItemsInput;
  });

  return {
    orderDetails,
    bodySide: mapBodySideToGql(data.bodySide),
    kneeJoint: data.kneeJoint || null,
    coverOrderItems,
  };
}

export const createOrder = async (orderRequestData: OrderRequestData, selectedModel: Model2, isDraft: boolean) => {
  try {
    const supplier = new URLSearchParams(window.location.search).get('supplier') as string;
    const publicDomain = await getDomainByUrlSuffix(supplier);
    const user = await checkUserSession();
    const address = (await UserService.getUserByEmail(user?.email))?.addresses?.[0];
    
    const order = mapToCoverOrderInput
      (
        orderRequestData, 
        selectedModel.components,
        isDraft, 
        publicDomain.domainId, 
        user.pk,
        address?.addressId ?? 0,
        selectedModel.modelId
      );
        
    const draftOrder = JSON.parse(localStorage.getItem("draftOrder") ?? "null");
    if(draftOrder === null) {
      const createdOrder = await addCoverOrder(order);
      localStorage.setItem('draftOrder', JSON.stringify(createdOrder));
    }
    else {
      await updateCoverOrder(draftOrder.orderId, order);
    }
  } catch (error) {
    console.error(error);
  }
}